/**
 * Gedeelde cart-module: quick-add (form[data-quick-add]), aantal wijzigen en
 * verwijderen ([data-cart-change]-links in drawer en op /cart) via de Cart
 * AJAX API met section rendering; drawer, cart-pagina-sectie en header-teller
 * blijven in één keer in sync.
 *
 * Progressive enhancement: zonder JS posten de formulieren naar /cart/add en
 * navigeren de +/−/verwijder-links via /cart/change.
 *
 * Gedrag na toevoegen (theme setting cart_behavior op de drawer-dialog):
 * - 'drawer': altijd het zijpaneel openen
 * - 'page':   altijd door naar de winkelmand-pagina
 * - 'auto':   accessoire → zijpaneel; fiets (product.type != 'Accessoire') → pagina
 */

function cartDrawer() {
  return document.getElementById('cart-drawer');
}

function sectionIdOf(el) {
  const section = el?.closest('.shopify-section');
  return section ? section.id.replace('shopify-section-', '') : null;
}

/* Elementen die na een cart-mutatie ververst worden; de cart-pagina-sectie
   meldt zich via [data-cart-page] (deelstap 2). */
const SWAP_SELECTORS = ['#cart-drawer', '[data-cart-page]'];

function cartSectionIds() {
  const ids = new Set();
  for (const sel of SWAP_SELECTORS) {
    const id = sectionIdOf(document.querySelector(sel));
    if (id) ids.add(id);
  }
  return [...ids];
}

function applySectionHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  for (const sel of SWAP_SELECTORS) {
    const fresh = doc.querySelector(sel);
    const current = document.querySelector(sel);
    // innerHTML-swap zodat de open <dialog> open blijft
    if (fresh && current) current.innerHTML = fresh.innerHTML;
  }
}

/* ===== Gratis-verzending-tracker =====
   Werkt alle [data-fst]-instanties bij (announcement-bar, productpagina;
   drawer en winkelmand krijgen daarnaast verse server-HTML via de swap).
   Fiets in de mand (product_type != 'Accessoire') = altijd gratis verzending. */

function geldbedrag(cents) {
  const tekst = (cents / 100).toFixed(2).replace('.', ',').replace(/,00$/, '');
  return `€${tekst}`;
}

function updateVerzendTrackers(cart) {
  const totaal = Number(cart.total_price) || 0;
  const aantal = Number(cart.item_count) || 0;
  const aantalFietsen = (cart.items || [])
    .filter((item) => item.product_type !== 'Accessoire')
    .reduce((som, item) => som + (Number(item.quantity) || 0), 0);
  for (const el of document.querySelectorAll('[data-fst]')) {
    const drempel = Number(el.dataset.drempel);
    if (!drempel) continue;
    const status =
      aantal === 0 ? 'leeg'
      : aantalFietsen > 1 ? 'fietsen'
      : aantalFietsen === 1 ? 'fiets'
      : totaal >= drempel ? 'gehaald'
      : 'onderweg';
    const rest = Math.max(0, drempel - totaal);
    const procent = status === 'onderweg' ? Math.min(100, Math.round((totaal / drempel) * 100)) : status === 'leeg' ? 0 : 100;
    el.dataset.status = status;
    for (const bedrag of el.querySelectorAll('[data-fst-bedrag]')) bedrag.textContent = geldbedrag(rest);
    for (const vulling of el.querySelectorAll('[data-fst-vulling]')) vulling.style.width = `${procent}%`;
    const balk = el.querySelector('[role="progressbar"]');
    if (balk) {
      balk.setAttribute('aria-valuenow', String(procent));
      const label = el.querySelector(`.fsb__regel--${status === 'leeg' ? 'onderweg' : status}`);
      if (label) balk.setAttribute('aria-label', label.textContent.replace(/\s+/g, ' ').trim());
    }
    // Announcement-bar: met iets in de mand blijft de tracker gepind in beeld
    const rotatiebalk = el.closest('[data-announcement-bar]');
    if (rotatiebalk) rotatiebalk.classList.toggle('announcement-bar--tracker-vast', status !== 'leeg');
  }
}

function updateHeaderCount(count) {
  const button = document.querySelector('.site-header__cart');
  if (!button) return;
  const badge = button.querySelector('[data-cart-count]');
  if (badge) {
    badge.textContent = count;
    badge.classList.toggle('is-empty', count === 0);
  }
  const label = button.getAttribute('aria-label');
  if (label) button.setAttribute('aria-label', label.replace(/\d+/, count));
}

/** Ververs drawer + cart-pagina + teller buiten een mutatie om (na add). */
async function refreshCart() {
  const ids = cartSectionIds();
  const tasks = [];
  if (ids.length) {
    tasks.push(
      fetch(`${window.location.pathname}?sections=${ids.join(',')}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((sections) => {
          if (sections) Object.values(sections).forEach(applySectionHtml);
        })
    );
  }
  tasks.push(
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((cart) => {
        updateHeaderCount(cart.item_count);
        updateVerzendTrackers(cart);
      })
  );
  await Promise.allSettled(tasks);
}

// Terug via de browsercache (bfcache): mand kan intussen veranderd zijn
window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshCart();
});

function ringBell() {
  const bell = document.querySelector('.site-header__cart');
  if (!bell) return;
  bell.classList.remove('is-ringing');
  void bell.offsetWidth;
  bell.classList.add('is-ringing');
}

/** Meldt een cart-actie aan screenreaders via de aria-live-regio in theme.liquid. */
function meldCartActie() {
  const regio = document.querySelector('[data-cart-status]');
  if (!regio) return;
  regio.textContent = '';
  setTimeout(() => {
    regio.textContent = regio.dataset.tekstToegevoegd;
  }, 60);
}

function setBusy(busy) {
  for (const sel of SWAP_SELECTORS) {
    const el = document.querySelector(sel);
    if (el) el.setAttribute('aria-busy', busy ? 'true' : 'false');
  }
}

/* ===== Aantal wijzigen / verwijderen ===== */

let mutatieBezig = false;

document.addEventListener('click', async (event) => {
  const link = event.target.closest('[data-cart-change]');
  if (!link) return;
  event.preventDefault();
  // regelnummers verschuiven na een wijziging: nooit twee mutaties tegelijk
  if (mutatieBezig) return;
  mutatieBezig = true;
  setBusy(true);

  // de innerHTML-swap gooit de toetsenbord-focus weg; positie onthouden zodat
  // we na de verversing op dezelfde plek verder kunnen (regel weg → volgende)
  const container = link.closest(SWAP_SELECTORS.join(','));
  const hadFocus = document.activeElement === link;
  const focusIdx = container ? [...container.querySelectorAll('[data-cart-change]')].indexOf(link) : -1;

  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line: Number(link.dataset.line),
        quantity: Number(link.dataset.quantity),
        sections: cartSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) throw new Error(`Bijwerken mislukt: ${res.status}`);
    const cart = await res.json();
    Object.values(cart.sections || {}).forEach(applySectionHtml);
    updateHeaderCount(cart.item_count);
    updateVerzendTrackers(cart);
    setBusy(false);
    if (hadFocus && container) {
      const verse = [...container.querySelectorAll('[data-cart-change]')];
      const doel = verse[focusIdx] ?? verse[verse.length - 1] ?? container.querySelector('a[href], button');
      doel?.focus({ preventScroll: true });
    }
  } catch {
    window.location.href = link.href; // val terug op de no-JS-route
    return;
  } finally {
    mutatieBezig = false;
  }
});

/* ===== Kortingscode (op /cart) =====
   Met JS: toepassen via de /discount-permalink en terug naar de mand.
   Zonder JS: het formulier GET't naar /checkout?discount=CODE. */

document.addEventListener('submit', (event) => {
  const form = event.target.closest('form[data-discount-form]');
  if (!form) return;
  event.preventDefault();
  const code = form.querySelector('input[name="discount"]')?.value.trim();
  if (!code) return;
  window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent('/cart')}`;
});

/* ===== Quick-add ===== */

/** Toont een foutregel in het formulier (bv. variant net uitverkocht). */
function toonFormFout(form, tekst) {
  if (!tekst) return;
  let fout = form.querySelector('.form-fout');
  if (!fout) {
    fout = document.createElement('p');
    fout.className = 'form-fout';
    fout.setAttribute('role', 'alert');
    form.append(fout);
  }
  fout.textContent = tekst;
}

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form[data-quick-add]');
  if (!form) return;
  event.preventDefault();

  const button = form.querySelector('button[type="submit"]');
  if (button?.classList.contains('is-loading')) return;
  button?.classList.add('is-loading');
  form.querySelector('.form-fout')?.remove();

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json' },
    });
    if (res.status === 422) {
      // Variant niet (meer) leverbaar: nette melding in het formulier in
      // plaats van doorsturen naar Shopify's kale foutpagina
      let beschrijving = null;
      try { beschrijving = (await res.json()).description; } catch { /* geen json */ }
      toonFormFout(form, beschrijving || form.dataset.foutTekst || cartDrawer()?.dataset.foutTekst);
      button?.classList.remove('is-loading');
      return;
    }
    if (!res.ok) throw new Error(`Toevoegen mislukt: ${res.status}`);

    const drawer = cartDrawer();
    const behavior = drawer?.dataset.cartBehavior || 'auto';
    const productType = form.dataset.productType || '';
    const isFiets = productType !== '' && productType !== 'Accessoire';
    const opCartPagina = Boolean(document.querySelector('[data-cart-page]'));
    const naarPagina = behavior === 'page' || (behavior === 'auto' && isFiets);

    if (naarPagina && !opCartPagina) {
      window.location.href = drawer?.dataset.cartUrl || '/cart';
      return;
    }

    await refreshCart();
    button?.classList.remove('is-loading');
    button?.classList.add('is-added');
    setTimeout(() => button?.classList.remove('is-added'), 1600);

    ringBell();
    meldCartActie();
    if (drawer && !drawer.open && !opCartPagina) drawer.showModal();
  } catch {
    button?.classList.remove('is-loading');
    form.submit(); // val terug op de normale POST; Shopify toont zelf de fout
  }
});
